import { createRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { flushPendingSignUp } from "@/lib/analytics";
import { authClient } from "@/lib/auth";
import { trpcClient } from "@/lib/trpc";
import { promiseSleep } from "@/lib/utils";
import { createTeamRoute } from "./createTeamRoute";
import { loginRoute } from "./loginRoute";
import { rootRoute } from "./rootRoute";

export const joinTeamRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/join-team",
    beforeLoad: async () => {
        const session = await authClient.getSession();
        if (!session.data?.user) {
            throw redirect({
                to: loginRoute.to,
                search: {
                    redirect: window.location.href,
                },
            });
        }

        flushPendingSignUp(session.data.user);

        const pendingInvitations = await trpcClient.auth.getPendingInvitations.query();
        if (pendingInvitations.length === 0) {
            throw redirect({
                to: createTeamRoute.to,
            });
        }

        return { pendingInvitations };
    },
    component: JoinTeamPage,
});

function JoinTeamPage() {
    const { pendingInvitations } = joinTeamRoute.useRouteContext();
    const [acceptingId, setAcceptingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleAccept = async (invitationId: string) => {
        setAcceptingId(invitationId);
        setError(null);

        await promiseSleep(1000); // fake to show working
        const { error } = await authClient.organization.acceptInvitation({
            invitationId,
        });

        if (error) {
            setError(error.message ?? "Failed to accept invitation");
            setAcceptingId(null);
            return;
        }

        window.location.href = "/";
    };

    return (
        <div className="bg-muted flex min-h-svh min-w-svw flex-col items-center justify-center gap-6 p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
                <Card>
                    <CardHeader className="text-center">
                        <CardTitle className="text-xl">You've been invited!</CardTitle>
                        <CardDescription>You have pending invitations to join a team.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4">
                            {error && (
                                <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                                    {error}
                                </div>
                            )}

                            {pendingInvitations.map((invitation) => (
                                <div
                                    key={invitation.id}
                                    className="flex items-center justify-between rounded-lg border p-4"
                                >
                                    <div>
                                        <div className="font-medium">{invitation.organizationName}</div>
                                        {invitation.role && (
                                            <div className="text-sm text-muted-foreground capitalize">
                                                {invitation.role}
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        size="sm"
                                        disabled={acceptingId !== null}
                                        onClick={() => handleAccept(invitation.id)}
                                    >
                                        {acceptingId === invitation.id ? "Joining..." : "Join team"}
                                    </Button>
                                </div>
                            ))}

                            <div className="relative my-2">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-card px-2 text-muted-foreground">or</span>
                                </div>
                            </div>

                            <Button
                                variant="outline"
                                className="w-full"
                                nativeButton={false}
                                render={<a href={createTeamRoute.to}>Create your own team</a>}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
